import { CalendarIcon, ChevronRightIcon } from '@heroicons/react/outline'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { flatten, pathOr, propOr } from 'ramda'
import { useEffect, useMemo, useState } from 'react'

import useProgramRegistrations from '@/app/dashboard/events/stores/use-program-registrations'
import Link from '@/components/link'
import moderators from '@/config/moderators'
import useNotification from '@/modules/notifications/stores/use-notification'
import useSwr from '@/modules/swr/hooks/use-swr'
import useProfile from '@/modules/user/stores/use-profile'

import ProgramApis from '../../apis'
import { getActiveRegistrations } from '../../utils/active-registrations'
import useProgram from '../stores/use-program'

const programSelector = propOr({}, 'program')
const registrationsSelector = propOr([], 'registrations')
const userIdSelector = pathOr('', ['profile', 'userId'])

const RegisteredSessions = () => {
  const {
    query: { handle },
  } = useRouter()
  const [loading, setloading] = useState(true)
  const userId = useProfile(userIdSelector)
  const program = useProgram(programSelector)
  const registrations = useProgramRegistrations(registrationsSelector)
  const addNotification = useNotification.use.addNotification()
  const isModerator = useMemo(() => moderators.includes(userId), [userId])
  const router = useRouter()
  const { data } = useSwr({
    key: `program-${handle}-registration-options`,
    initialData: [],
    fetcher: () => ProgramApis.getRegistrationOptions(handle),
    condition: isModerator,
  })

  const activeRegistrations = useMemo(
    () => getActiveRegistrations(program.id, registrations),
    [program.id, registrations],
  )
  const convertToDS = (dateString) =>
    parseInt(
      `${dateString.getFullYear()}${String(dateString.getMonth()).padStart(
        2,
        '0',
      )}${String(dateString.getDate()).padStart(2, '0')}`,
      10,
    )

  const convertToTS = (dateString) =>
    parseInt(
      `${dateString.getHours()}${String(dateString.getMinutes()).padStart(
        2,
        '0',
      )}`,
      10,
    )
  const checkSession = (dateString) => {
    const givenDate = new Date(dateString)
    const before10Min = new Date(dateString)
    before10Min.setMinutes(before10Min.getMinutes() - 10)
    const after45Min = new Date(dateString)
    after45Min.setMinutes(after45Min.getMinutes() + 45)
    const toDate = new Date()
    if (convertToDS(toDate) < convertToDS(givenDate)) {
      return 'Coming up'
    }
    if (
      convertToDS(toDate) === convertToDS(givenDate) &&
      convertToTS(toDate) >= convertToTS(before10Min) &&
      convertToTS(toDate) <= convertToTS(after45Min)
    ) {
      return 'In progress'
    }
    if (
      convertToDS(toDate) === convertToDS(givenDate) &&
      convertToTS(toDate) <= convertToTS(before10Min)
    ) {
      return "Today's classes"
    }

    return 'Completed'
  }
  const activeSession = (dateString, handler, sessionId, sessionTime) => {
    if (dateString === 'In progress') {
      return router.push(`/program/${handler}/session/${sessionId}`)
    }
    if (dateString === 'Completed') {
      // eslint-disable-next-line no-alert
      return addNotification('', 'This session is already completed')
    }

    // eslint-disable-next-line no-alert
    return addNotification(
      '',
      `Your next class is scheduled at  ${DateTime.fromISO(
        sessionTime,
      ).toFormat('dd MMM yyyy')}-${DateTime.fromISO(sessionTime).toFormat(
        'h:mm a',
      )}. You can join the class room 10 minutes ahead of time`,
    )
  }

  const sessions = flatten(
    (isModerator ? data : activeRegistrations).map((reg) =>
      (reg?.registrationOption?.schedule || reg?.schedule)?.map((schedule) => ({
        ...schedule,
        program: reg.program || program,
        registrationOption: reg.registrationOption || reg,
      })),
    ),
  )

  useEffect(() => {
    const timeout = setTimeout(() => setloading(false), 3000)

    return () => clearTimeout(timeout)
  }, [loading, registrations])
  return !activeRegistrations?.length && !isModerator ? null : loading ? (
    <div className='flex items-center justify-center'>
      <div role='status'>
        <svg
          aria-hidden='true'
          className='mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600'
          viewBox='0 0 100 101'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
            fill='currentColor'
          />
          <path
            d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
            fill='currentFill'
          />
        </svg>
        <span className='sr-only'>Loading...</span>
      </div>
    </div>
  ) : (
    <div className='my-4 py-4'>
      <p className='mb-4'>Registered Sessions</p>
      <ul className='space-y-2'>
        {sessions.map((session) => (
          <li
            key={session.id}
            id={`session-${DateTime.fromISO(session.startTime).toFormat(
              'dd-MMM-yyyy',
            )}-${DateTime.fromISO(session.startTime).toFormat('h-mm-a')}`}
          >
            <button
              type='button'
              value={checkSession(session.startTime)}
              onClick={(e) => {
                e.preventDefault()
                activeSession(
                  e.currentTarget.value,
                  handle,
                  session.id,
                  session.startTime,
                )
              }}
              className='block w-full rounded-md bg-white'
            >
              <div className='flex items-center px-4 py-4 sm:px-6'>
                <div className='min-w-0 flex-1 sm:flex sm:items-center sm:justify-between'>
                  <div className='truncate'>
                    <div className='flex text-sm'>
                      <p className='truncate font-medium text-theme-blue'>
                        {DateTime.fromISO(session.startTime).toFormat(
                          `dd MMM yyyy`,
                        )}
                      </p>
                      <p className='ml-1 flex-shrink-0 font-normal text-gray-500'>
                        - {session.registrationOption?.title}
                      </p>
                    </div>
                    <div className='mt-2 flex'>
                      <div className='flex items-center text-sm text-gray-500'>
                        <CalendarIcon
                          className='mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400'
                          aria-hidden='true'
                        />
                        <p>
                          Starts on{' '}
                          <time dateTime={session.startTime}>
                            {DateTime.fromISO(session.startTime).toFormat(
                              `h:mm a`,
                            )}
                          </time>
                          &nbsp;
                          <b className='text-black'>
                            ({checkSession(session.startTime)})
                          </b>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='ml-5 flex-shrink-0'>
                  <div className='flex'>
                    <ChevronRightIcon
                      className='h-5 w-5 text-gray-400'
                      aria-hidden='true'
                    />
                  </div>
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default RegisteredSessions
